<template>
  <div>
    <NuxtLayout name="default">
      <HeroStandardApollo
        id="asuonline-students"
        :title="$page?.accreditation.title!"
        :bg-image-source="$page?.accreditation.heroImage!"
        :show-text="false"
        title-size="large"
        :display-gradient="true"
        :gradient-opacity="0.8"
        :full-width="false"
      ></HeroStandardApollo>

      <section
        class="pt-space-md pb-space-lg pt-lg-space-xxl pb-lg-space-xl bg-white"
      >
        <div class="container px-lg-0">
          <div class="row">
            <div class="col-12">
              <VideoModalAtlas
                :video-source="videoEmbedUrl"
                :bg-image-source="
                  $page?.accreditation?.accreditationVideo?.imageSource!
                "
                :title="$page?.accreditation?.accreditationVideo?.title!"
                :display-border="false"
                @modal-open="
                  handleModalEvent(
                    'open',
                    'is asu online accredited?',
                    'play button'
                  )
                "
                @modal-close="
                  handleModalEvent(
                    'close',
                    'is asu online accredited?',
                    'close cross'
                  )
                "
                class="mb-space-xxs mb-lg-space-xl"
              >
                <div
                  v-html="$page?.accreditation?.accreditationVideo?.description"
                ></div>
              </VideoModalAtlas>
            </div>
          </div>
          <div class="row gx-0 bg-light-2">
            <div class="col-12">
              <div class="pt-space-md px-space-sm">
                <div class="bg-secondary title-underline mb-space-sm"></div>
                <div
                  class="fs-medium pb-space-sm"
                  v-html="$page?.accreditation?.videoDescription"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- state authorization section -->
      <SectionParallaxAtlas
        :title="$page?.accreditation.authorizationTitle!"
        :bg-image-source="$page?.accreditation.authorizationImage!"
        container-bg-variant="white"
      >
        <template #text>
          <div
            class="mb-space-sm"
            v-html="$page?.accreditation.authorizationDescription"
          ></div>
          <div class="mb-space-md mb-lg-space-lg">
            <NuxtLink
              :href="$page?.accreditation?.authorizationLink?.uri!"
              role="button"
              class="btn btn-secondary fw-bold fs-medium px-space-xs"
              @click.native.prevent="handleAuthorizationClick"
            >
              {{ $page?.accreditation?.authorizationLink?.text }}
            </NuxtLink>
          </div>
          <div class="row">
            <div
              class="col-12"
              v-for="(card, index) in $page?.accreditation.authorizationCards"
              v-bind:key="index"
              :class="{
              'mb-space-sm mb-lg-space-md': !isLastItem(
                index,
                $page?.accreditation?.authorizationCards?.length!
              )
            }"
            >
              <CardInfoHorizontal
                border-variant="light-4"
                title-level="h2"
                title-size="small"
                :title="card?.title!"
                :bg-image-source="card?.image!"
                :display-label="false"
                :cta-as-button="true"
                cta-button-variant="secondary"
                cta-text-size="medium"
                cta-text-variant="dark-3"
                cta-text-weight="bold"
                :cta-text="card?.link?.text!"
                :cta-link="card?.link?.uri!"
                :display-cta-button="true"
                :highlight-width="8"
                @ctaClick="($event) => handleCardCtaClick($event, card)"
              >
                <div class="mb-space-sm" v-html="card?.text"></div>
              </CardInfoHorizontal>
            </div>
          </div>
        </template>
      </SectionParallaxAtlas>

      <!-- additional information section -->
      <SectionInformation
        :title="$page?.accreditation.questionsTitle!"
        :description="$page?.accreditation.questionsDescription!"
        :background-image="$page?.accreditation.questionsImage!"
      >
      </SectionInformation>
      <!-- section-rfi-multi-step -->
      <SectionRfiMultiStep :bg-image="RfiBgImage"></SectionRfiMultiStep>
    </NuxtLayout>
  </div>
</template>

<script setup lang="ts">
import { analyticsComposable } from "@rds-vue-ui/analytics-gs-composable";
import { CardInfoHorizontal } from "@rds-vue-ui/card-info-horizontal";
import HeroStandardApollo from "@rds-vue-ui/hero-standard-apollo/HeroStandardApollo.vue";
import { SectionParallaxAtlas } from "@rds-vue-ui/section-parallax-atlas";
import { VideoModalAtlas } from "@rds-vue-ui/video-modal-atlas";
import RfiBgImage from "~/assets/img/rfi-bg-overlay.png";
import SectionInformation from "~/components/about-us/SectionInformation.vue";
import SectionRfiMultiStep from "~/components/rfi/SectionRfiMultiStep.vue";
import { useUtilityService } from "~/composables/useUtilityService";
import {
  Accreditation,
  Maybe,
  ParagraphCardImage,
} from "~/server/resolvers/resolvers-types";

definePageMeta({
  layout: false,
  __dangerouslyDisableSanitizersByTagID: {
    "ldjson-schema": ["innerHTML"],
  },
});

const config = useRuntimeConfig();

const UtilityService = useUtilityService();

interface AccreditationResponse {
  accreditation: Accreditation;
}

const $page = await usePageQuery<AccreditationResponse>(
  `#graphql
      query accreditation($accreditationId: ID!){
        accreditation(id:$accreditationId) {
          id
          detailPage
          metaTitle
          metaDescription
          heroImage
          title
          accreditationVideo {
            id
            title
            description
            youtubeId
            imageSource
            imageAlt
            uploadDate
            duration
            captionText
          }
          videoDescription
          authorizationTitle
          authorizationImage
          authorizationDescription
          authorizationLink {
            text
            target
            uri
          }
          authorizationCards {
            title
            text
            image
            imageAlt
            link {
              text
              target
              uri
            }
          }
          questionsTitle
          questionsDescription
          questionsImage
        }
      }`,
  {
    accreditationId: "7bfbe5bd-b44b-4dd0-887c-2eb9fd9b6d10",
  },
  {
    key: "accreditation-page",
  }
);

useHead({
  title: $page?.accreditation.metaTitle,
  meta: [
    { name: "Title", content: $page?.accreditation.metaTitle },
    {
      name: "Description",
      content: $page?.accreditation.metaDescription,
    },
  ],
  link: [
    {
      rel: "canonical",
      href: UtilityService.formatCanonicalUrl(
        `${config.public.siteUrl}${$page?.accreditation.detailPage}`
      ),
    },
  ],
  script: [
    useSchemaService().generateAboutUsAccreditationSchema($page?.accreditation),
  ],
});

const handleModalEvent = (action: string, section: string, text: string) => {
  analyticsComposable.trackModalEvent(
    "onclick",
    action,
    "click",
    "main content",
    section.toLowerCase(),
    text,
    "video-modal"
  );
};

const isLastItem = (index: number, length: number) => {
  return index === length - 1;
};

const handleCardCtaClick = (event: Event, card: Maybe<ParagraphCardImage>) => {
  event.preventDefault();
  if (
    card &&
    card.title &&
    card.link &&
    card.link.text &&
    card.link.target &&
    card.link.uri
  ) {
    analyticsComposable.trackLinkEvent(
      "internal link",
      "main content",
      card.title.toLowerCase(),
      card.link.text.toLowerCase()
    );
    navigateTo(card.link.uri, {
      external: true,
      open: {
        target: card.link.target,
      },
    });
  } else {
    console.error("card missing link target or href");
  }
};

const handleAuthorizationClick = (event: Event) => {
  const linkTarget = event.target as HTMLLinkElement;

  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    $page?.accreditation?.authorizationTitle!.toLowerCase()!,
    $page?.accreditation?.authorizationLink?.text!.toLowerCase()!
  );
  if (linkTarget.href) {
    navigateTo(linkTarget.href, {
      open: {
        target: linkTarget.target ? linkTarget.target : "_self",
      },
    });
  }
};

const videoEmbedUrl = computed(() => {
  return `https://www.youtube.com/embed/${$page?.accreditation?.accreditationVideo?.youtubeId}`;
});
</script>

<style lang="scss" scoped>
.title-underline {
  width: 65px;
  height: 10px;
}
</style>
